import { useEffect } from 'react';
import { useNavigation } from '@react-navigation/native';

export type PageMeta = {
  /** The <title> shown in the browser tab / search results. */
  title: string;
  /** The <meta name="description"> text. */
  description?: string;
};

// react-native's TypeScript config ships no DOM lib, so reach the browser
// APIs through globalThis with minimal local shapes (same trick as
// index.web.tsx). Everything used here also exists in every evergreen
// browser, so this module must only ever be bundled for the web build.
type BrowserElement = {
  setAttribute(name: string, value: string): void;
  appendChild(child: unknown): void;
};

type BrowserDocument = {
  title: string;
  querySelector(selectors: string): BrowserElement | null;
  createElement(tagName: string): BrowserElement;
  head: BrowserElement | null;
};

type BrowserLocation = {
  origin: string;
  pathname: string;
};

const upsertTag = (
  documentRef: BrowserDocument,
  kind: 'meta' | 'link',
  selector: string,
  attributes: Record<string, string>,
) => {
  let tag = documentRef.querySelector(selector);
  if (!tag) {
    tag = documentRef.createElement(kind);
    documentRef.head?.appendChild(tag);
  }
  for (const [name, value] of Object.entries(attributes)) {
    tag.setAttribute(name, value);
  }
};

const applyPageMeta = ({ title, description }: PageMeta) => {
  const documentRef = (globalThis as { document?: BrowserDocument }).document;
  if (!documentRef) {
    return;
  }

  documentRef.title = title;

  if (description) {
    upsertTag(documentRef, 'meta', 'meta[name="description"]', {
      name: 'description',
      content: description,
    });
  }

  // The static <link rel="canonical"> in index.html already covers the home
  // URL; the SPA shell serves the same document for every route, so keep the
  // canonical pointing at the URL currently in the address bar.
  const location = (globalThis as { location?: BrowserLocation }).location;
  if (location) {
    upsertTag(documentRef, 'link', 'link[rel="canonical"]', {
      rel: 'canonical',
      href: `${location.origin}${location.pathname}`,
    });
  }
};

/**
 * Keeps the browser's per-page metadata (document title, meta description,
 * canonical) in sync with the focused screen.
 *
 * A screen that has been navigated away from stays mounted (the previous
 * screen in the stack is kept around), so a plain mount effect would leave
 * the previous screen's metadata in place when the user comes back. Applying
 * the metadata on the navigation 'focus' event (plus on mount, for the
 * initially focused screen) covers both cases.
 */
export const usePageMeta = ({ title, description }: PageMeta) => {
  const navigation = useNavigation();

  useEffect(() => {
    const update = () => applyPageMeta({ title, description });

    if (navigation.isFocused()) {
      update();
    }

    return navigation.addListener('focus', update);
  }, [navigation, title, description]);
};

